import type {
  BusinessCenterDocumentFolderKind,
  BusinessCenterDocumentRecord,
  BusinessCenterDocumentStatus,
  BusinessCenterDocumentType,
} from "../../lib/types";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const documentStatusOptions: BusinessCenterDocumentStatus[] = ["Draft", "Uploaded", "In Review", "Approved", "Archived"];
export const documentTypeOptions: BusinessCenterDocumentType[] = ["Proposal", "Agreement", "Invoice", "Brief", "Reference", "Other"];
export const documentFolderKindOptions: BusinessCenterDocumentFolderKind[] = ["Client", "Project", "Shared"];

const documentsStore = createBusinessCenterOperationalStateStore<BusinessCenterDocumentRecord[]>({
  moduleKey: "business_center_documents",
  scope: { scopeKey: "document-center", scopeType: "platform" },
  seed: createSeedDocuments,
  normalize: (documents) => documents.map(normalizeDocument),
  isEmpty: (documents) => !Array.isArray(documents) || documents.length === 0,
});

export function loadBusinessCenterDocuments(): BusinessCenterDocumentRecord[] {
  return documentsStore.read();
}

export async function refreshBusinessCenterDocuments() {
  return documentsStore.refresh();
}

export function loadBusinessCenterDocumentsForBrand(brand: BusinessCenterDocumentRecord["brand"]) {
  return loadBusinessCenterDocuments().filter((document) => document.brand === brand);
}

export interface BusinessCenterDocumentInput {
  brand: BusinessCenterDocumentRecord["brand"];
  folderKind: BusinessCenterDocumentFolderKind;
  clientId?: string | null;
  clientName?: string;
  projectId?: string | null;
  projectName?: string;
  documentName: string;
  documentType: BusinessCenterDocumentType;
  status?: BusinessCenterDocumentStatus;
  notes?: string;
}

export function createBusinessCenterDocument(input: BusinessCenterDocumentInput): BusinessCenterDocumentRecord {
  const now = new Date().toISOString();
  const documentCount = loadBusinessCenterDocuments().filter((document) => document.brand === input.brand).length + 1;
  return normalizeDocument({
    id: crypto.randomUUID(),
    documentNumber: `BCD-${String(documentCount).padStart(4, "0")}`,
    brand: input.brand,
    folderKind: input.folderKind,
    clientId: input.clientId ?? null,
    clientName: input.clientName || "Unlinked Client",
    projectId: input.projectId ?? null,
    projectName: input.projectName || "Unlinked Project",
    documentName: input.documentName || "Untitled Document",
    documentType: input.documentType || "Other",
    status: input.status || "Draft",
    notes: input.notes || "",
    createdAt: now,
    updatedAt: now,
  });
}

export function upsertBusinessCenterDocument(nextDocument: BusinessCenterDocumentRecord) {
  const current = loadBusinessCenterDocuments();
  const normalized = normalizeDocument({ ...nextDocument, updatedAt: new Date().toISOString() });
  const exists = current.some((document) => document.id === normalized.id);
  const documents = exists ? current.map((document) => (document.id === normalized.id ? normalized : document)) : [normalized, ...current];
  saveBusinessCenterDocuments(documents);
  return documents;
}

export function updateBusinessCenterDocument(documentId: string, changes: Partial<BusinessCenterDocumentRecord>) {
  const current = loadBusinessCenterDocuments();
  const documents = current.map((document) =>
    document.id === documentId
      ? normalizeDocument({
          ...document,
          ...changes,
          updatedAt: new Date().toISOString(),
        })
      : document,
  );
  saveBusinessCenterDocuments(documents);
  return documents;
}

export function removeBusinessCenterDocument(documentId: string) {
  const documents = loadBusinessCenterDocuments().filter((document) => document.id !== documentId);
  saveBusinessCenterDocuments(documents);
  return documents;
}

function saveBusinessCenterDocuments(documents: BusinessCenterDocumentRecord[]) {
  documentsStore.write(documents);
}

function normalizeDocument(document: BusinessCenterDocumentRecord): BusinessCenterDocumentRecord {
  return {
    ...document,
    brand: document.brand || "truevance_connect",
    folderKind: documentFolderKindOptions.includes(document.folderKind) ? document.folderKind : "Shared",
    clientId: document.clientId ?? null,
    clientName: document.clientName || "Unlinked Client",
    projectId: document.projectId ?? null,
    projectName: document.projectName || "Unlinked Project",
    documentName: document.documentName || "Untitled Document",
    documentType: documentTypeOptions.includes(document.documentType) ? document.documentType : "Other",
    status: documentStatusOptions.includes(document.status) ? document.status : "Draft",
    notes: document.notes || "",
    createdAt: document.createdAt || new Date().toISOString(),
    updatedAt: document.updatedAt || new Date().toISOString(),
  };
}

function createSeedDocuments(): BusinessCenterDocumentRecord[] {
  const now = new Date().toISOString();
  return [
    normalizeDocument({
      id: crypto.randomUUID(),
      documentNumber: "BCD-0001",
      brand: "truevance_connect",
      folderKind: "Client",
      clientName: "Northstar Ministries",
      projectName: "Northstar Quick Wins Roadmap",
      documentName: "Discovery summary deck",
      documentType: "Proposal",
      status: "Approved",
      notes: "Placeholder document record for the client folder structure.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeDocument({
      id: crypto.randomUUID(),
      documentNumber: "BCD-0002",
      brand: "truevance_connect",
      folderKind: "Project",
      clientName: "Summit Event Group",
      projectName: "Operations Dashboard Build",
      documentName: "Implementation brief",
      documentType: "Brief",
      status: "In Review",
      notes: "Reserved for project files that will later connect to storage.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeDocument({
      id: crypto.randomUUID(),
      documentNumber: "BCD-1001",
      brand: "kreatethedate",
      folderKind: "Client",
      clientName: "Amara Thompson",
      projectName: "Memory Media Vault Setup",
      documentName: "Vault setup checklist",
      documentType: "Reference",
      status: "Uploaded",
      notes: "Safe placeholder record that does not change vault behavior.",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeDocument({
      id: crypto.randomUUID(),
      documentNumber: "BCD-1002",
      brand: "kreatethedate",
      folderKind: "Shared",
      clientName: "Unlinked Client",
      projectName: "Unlinked Project",
      documentName: "Invoice template reference",
      documentType: "Invoice",
      status: "Draft",
      notes: "Used to represent shared documents in the foundation layer.",
      createdAt: now,
      updatedAt: now,
    }),
  ];
}
